import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Polling, type Rest, type RestJob, HttpService, PollingService } from '@dm/core';
import { type Observable } from 'rxjs';

export interface TaskData {
    id: string;
    name: string;
    objName: string;
    taskStatus: Status;
    progress: number;
    creator: string;
    userSession: string;
    startTime: string;
    endTime: string;
    duration: string;
    stepsNum: number;
    steps: StepData[];
    // 交叉类型，两种类型的叠加
    description: {
        currentStepIndex: number;
        stepCount: number;
        stepNames: string[];
    } & string;
    displayName: string;
    vstoreId?: string;
}

export interface ErrorData {
    name?: string;
    code: string;
    description: string;
    suggestion: string;
    useNewTemplate?: boolean;
}

export interface StepData {
    name: string;
    displayName: string;
    objName: string;
    execStatus: Status;
    startTime: string;
    endTime: string;
    duration: string;
    description: ErrorData;
}

export enum Status {
    waiting = 'wait',   // 等待执行
    running = 'executing', // 执行中;
    success = 'success', // 执行成功;
    failed = 'fail', // 执行失败;
    rollbackSuccess = 'rollback_success',  // 回滚成功;
    rollbackFailed = 'rollback_fail', // 回滚失败;
    rollbacking = 'rollback', // 回滚中;
    particalFailed = 'partial_fail', // 部分失败
    timing = 'timing'
}

export interface TaskModel {
    /** 是否正在加载数据 */
    isLoading: boolean;
    /** 任务数据 */
    tasks: TaskData[];
    /** 是否有任务数据 */
    noData: boolean;
    /** 是否有未读任务 */
    hasUnreadTasks: boolean;
}


@Injectable()
export class TaskCenterService {
    // 默认查询参数
    defaultParams = {
        range: '{ "offset": 0, "limit": 10 }',
        sort: '{"startTime": "desc"}',
    };

    constructor(
        private polling: PollingService,
        private http: HttpService,
    ) { }

    /**
     * 创建轮询请求
     * @param restJobs 轮询rest任务
     */
    pollData(restJobs: RestJob[], time = 15): Polling {
        return this.polling.addCombo(restJobs, time);
    }

    /**
     * 移除一个轮询
     * @param p 轮询对象
     */
    removePolling(p: Polling): void {
        if (p) {
            this.polling.remove(p);
        }
    }

    /**
     * 查询指定ID的任务信息
     * @param id 任务ID
     */
    getTask(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('task.get', 'V2').format(id);
        return this.http.get(rest, options);
    }

    /**
     * 批量查询任务信息
     * @param params 查询参数
     * @returns 可以查询该信息的轮询任务
     */
    batchGetTaskRestJob(params?: { [key: string]: string }): RestJob {
        const paramsData = params ?? this.defaultParams;
        const rest = this.http.makeUrl('task.batchGet', 'V2').setTempParams(paramsData);
        return { rest, method: 'GET' };
    }

    /**
     * 获取批量查询任务的rest
     */
    getBatchTaskRest(vstoreId?: string): Rest {
        return this.http.makeUrl('task.batchGet', 'V2').setFilters([
            { field: 'vstoreId', value: vstoreId, matchMode: '' },
        ]);
    }

    /**
     * 查询满足条件的任务数量
     * @param params 查询参数
     * @returns 可以查询该信息的轮询任务
     */
    getCountRestJob(params?: { [key: string]: string }): RestJob {
        const paramsData = params ?? this.defaultParams;
        const rest = this.http.makeUrl('task.getCount', 'V2').setTempParams(paramsData);
        return { rest, method: 'GET' };
    }

    /**
     * 获取任务子步骤信息
     * @param id 任务ID
     * @returns rest
     */
    getTaskStepsRest(id: string): Rest {
        const rest = this.http.makeUrl('task.getSteps', 'V2')
            .format(id);
        return rest;
    }

    /**
     * 删除单个任务
     * @param id 任务ID
     */
    deleteTask(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('task.delete', 'V2').format(id);
        return this.http.delete(rest, { doException: false });
    }

    /**
     * 回滚任务
     * @param id 任务ID
     */
    rollbackTask(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('task.rollback', 'V2');
        return this.http.post(rest, { id }, { doException: false });
    }

    /**
     * 重试任务
     * @param id 任务ID
     */
    retryTask(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('task.retry', 'V2');
        return this.http.post(rest, { id }, { doException: false });
    }

    /**
     * 轮询更新任务状态
     *
     * @param {BaseRequestOptionsArgs} [options]
     * @returns
     * @memberof TaskService
     */
    updatingTaskStatus(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('task.batchGet', 'V2');
        rest.setParams({
            filter: 'all',
        });
        return this.http.get(rest, options);
    }

    doException(rest: Rest, res: HttpResponse): void {
        this.http.handleException(rest, res);
    }

    /**
     * 查询任务的国际化名称
     */
    queryTaskName(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('task.queryTaskName', 'V2');
        return this.http.get(rest);
    }
}
